import { formatFloorArea, type PropertyCandidate } from '@/entities/property';
import { AddressLookupFields, FoundAddressCard } from '@/shared/ui';

import { inspectionsCopy as copy } from '../copy';
import type { ObjectAddress } from '../objectAddress';

/**
 * Het objectdeel van "Nieuwe keuring": het gedeelde adresblok
 * ({@link AddressLookupFields}), met bij een gevonden adres wat de BAG over het
 * pand weet: oppervlakte, bouwjaar en gebruiksdoel.
 */
export function ObjectAddressFields({ address }: { address: ObjectAddress }) {
    return (
        <AddressLookupFields
            address={address}
            copy={copy.objectAddress}
            describeCandidate={summary}
            renderFound={(candidate, onChooseAnother) => (
                <FoundAddressCard
                    candidate={candidate}
                    eyebrow={
                        candidate.known ? copy.newAddressKnownEyebrow : copy.objectAddress.found
                    }
                    otherLabel={copy.objectAddress.other}
                    onChooseAnother={onChooseAnother}
                >
                    <BuildingFacts candidate={candidate} complete={address.complete} />
                </FoundAddressCard>
            )}
        />
    );
}

/** Oppervlakte, bouwjaar en gebruiksdoel uit de BAG, en of het object al bekend is. */
function BuildingFacts({
    candidate,
    complete,
}: {
    candidate: PropertyCandidate;
    complete: boolean;
}) {
    const facts = [
        { label: copy.newAddressFloorArea, value: formatFloorArea(candidate.floorArea) },
        {
            label: copy.newAddressBuildYear,
            value:
                candidate.buildYear === null ? copy.newAddressUnknown : String(candidate.buildYear),
        },
        {
            label: copy.newAddressUsage,
            value: candidate.usagePurposeLabels.join(', ') || copy.newAddressUnknown,
        },
    ];

    return (
        <>
            <dl className="m-0 mt-3 grid grid-cols-[auto_auto_minmax(0,1fr)] gap-x-6 gap-y-1">
                {facts.map((fact) => (
                    <div key={fact.label} className="min-w-0">
                        <dt className="text-[12px] leading-[1.3] text-[var(--text-secondary)]">
                            {fact.label}
                        </dt>
                        <dd className="m-0 text-[14px] leading-[1.4] text-[var(--text-primary)]">
                            {fact.value}
                        </dd>
                    </div>
                ))}
            </dl>

            {candidate.known ? (
                <p className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--accent-dark)]">
                    {copy.newLookupKnown}
                </p>
            ) : null}
            {!complete ? (
                <p className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                    {copy.newAddressIncomplete}
                </p>
            ) : null}
        </>
    );
}

/** "64.446 m² · 2014 · Kantoorfunctie", of dat het object al in de dossiers staat. */
function summary(candidate: PropertyCandidate): string {
    return [
        candidate.known ? copy.newAddressKnownEyebrow : null,
        candidate.floorArea === null ? null : formatFloorArea(candidate.floorArea),
        candidate.buildYear === null ? null : String(candidate.buildYear),
        candidate.usagePurposeLabels.join(', ') || null,
    ]
        .filter(Boolean)
        .join(' · ');
}
